import React, { useState, useEffect, Fragment } from "react";
import {
  Row,
  Col,
  CardBody,
  Input,
  Table
} from "reactstrap";
import i18n from "../../../i18n";
import { useNavigate } from "react-router-dom";
import { getServiceRequestAdmin, putPlatformRequest } from "../../../helpers/api_rombo_helper";
import PropTypes from "prop-types";
import Flatpickr from "flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { Spanish } from "flatpickr/dist/l10n/es";

const Step1 = ({ token, infoUser, setErrorAlert, setErrorAlertMessage }) => {

  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [newDate, setNewDate] = useState("");
  const [newTime, setNewTime] = useState("");
  const [datePicker, setDatePicker] = useState(null);
  const [timePicker, setTimePicker] = useState(null);
  const [allowReschedule, setAllowReschedule] = useState(true);
  const [rescheduleMessage, setRescheduleMessage] = useState("");

  const fetchServiceData = async () => {
    const code = sessionStorage.getItem("rescheduleServiceCode");
  
    if (!code) return;
  
    try {
      const response = await getServiceRequestAdmin(token, code);
      setService(response);
      setNewDate(response.scheduled_start.date);
      setNewTime(response.scheduled_start.time);
      
      checkRescheduleAvailability(response);
    } catch (error) {
      console.error("Error - fetchServiceData - ", error);
      setErrorAlert(true);
      setErrorAlertMessage(error?.response?.data?.detail || error.message);
    }
  };

  const checkRescheduleAvailability = (serviceData) => {
    const now = new Date();
    const serviceDate = new Date(`${serviceData.scheduled_start.date}T${serviceData.scheduled_start.time}`);
    
    if (serviceDate < now) {
      setAllowReschedule(false);
      setRescheduleMessage(i18n.t("CannotReschedulePastService"));
      return;
    }
    
    const timeDiff = serviceDate - now;
    const minutesDiff = timeDiff / (1000 * 60);
    
    if (minutesDiff < 15) {
      setAllowReschedule(false);
      setRescheduleMessage(i18n.t("CannotRescheduleWithin30Minutes"));
      return;
    }
    
    setAllowReschedule(true);
    setRescheduleMessage("");
  };

  useEffect(() => {
    fetchServiceData();
  }, []);

  useEffect(() => {
    if (service && allowReschedule) {
      const dateInstance = Flatpickr("#datePicker", {
        dateFormat: "Y-m-d",
        defaultDate: service.scheduled_start.date,
        minDate: "today",
        locale: Spanish,
        onChange: (selectedDates, dateStr) => {
          setNewDate(dateStr);
        }
      });

      const timeInstance = Flatpickr("#timePicker", {
        enableTime: true,
        noCalendar: true,
        dateFormat: "H:i",
        defaultDate: service.scheduled_start.time,
        locale: Spanish,
        onChange: (selectedDates, timeStr) => {
          setNewTime(timeStr);
        }
      });

      setDatePicker(dateInstance);
      setTimePicker(timeInstance);

      return () => {
        dateInstance.destroy();
        timeInstance.destroy();
      };
    }
  }, [service, allowReschedule]);

  const handleReschedule = async () => {
    if (!allowReschedule) {
      setErrorAlert(true);
      setErrorAlertMessage(rescheduleMessage);
      return;
    }
  
    if (!newDate || !newTime) {
      setErrorAlert(true);
      setErrorAlertMessage(i18n.t("PleaseSelectNewDateAndTime"));
      return;
    }
  
    try {
      const requestBody = {
        requests: [
          {
            ...service,
            scheduled_start: {
              ...service.scheduled_start,
              date: newDate,
              time: newTime,
            },
          },
        ],
      };
  
      await putPlatformRequest(token, requestBody);
  
      localStorage.setItem("notify", "true");
      localStorage.setItem("headerText", i18n.t("ServiceRescheduledHeader"));
      localStorage.setItem("bodyText", i18n.t("ServiceRescheduledBody", { code: service.code }));
  
      if (infoUser.is_staff) {
        navigate("/services/general");
      } else {
        navigate("/services/request");
      }
    } catch (error) {
      console.error("Error - handleReschedule - ", error);
      setErrorAlert(true);
      setErrorAlertMessage(error?.response?.data?.detail || error.message);
    }
  };  

  const handleGoBack = () => {
    if (infoUser.is_staff) {
      navigate("/services/general");
    } else {
      navigate("/services/request");
    }
  };

  return (
    <Fragment>
      <CardBody>
        <Row>
          {infoUser.is_staff && (
            <Col xxl={12} md={12} xs={12}>
              <h6 className="employees-form-title" htmlFor="companyNameInput">
                {i18n.t("RescheduleServices")}
              </h6>
              <p className="employees-form-text">{i18n.t("SelectNewSchedules")}</p>
              {!allowReschedule && (
                <div className="alert alert-custom alert-dismissible fade show d-flex align-items-center mt-3" role="alert">
                  <div className="alert-icon-wrapper me-3">
                    <i className="bi bi-exclamation-triangle-fill alert-icon"></i>
                  </div>
                  <div className="alert-content flex-grow-1">
                    <h5 className="alert-heading mb-1">{i18n.t("Attention")}</h5>
                    <p className="mb-0">{rescheduleMessage}</p>
                  </div>
                </div>
              )}
              <div className="request-separator" />
              <div className="table-responsive pt-3">
                <Table className="table table-bordered mb-0 table-request-services">
                  <thead>
                    <tr>
                      <th className="company-column table-header2 padding-table">{i18n.t("Company")}</th>
                      <th className="request-number-column table-header2 padding-table">{i18n.t("ID")}</th>
                      <th className="origin-column table-header2 padding-table">{i18n.t("Origin")}</th>
                      <th className="destination-column table-header2 padding-table">{i18n.t("Destination")}</th>
                      <th className="date-column table-header2 padding-table">{i18n.t("Date")}</th>
                      <th className="hour-column table-header2 padding-table">{i18n.t("Hour")}</th>
                      <th className="type-column table-header2 padding-table">{i18n.t("Type")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {service && (
                      <tr>
                        <td>{service.partner_name}</td>
                        <td>{service.code}</td>
                        <td>{service.info.origin.address.description}</td>
                        <td>{service.info?.destination?.address?.description ?? ""}</td>
                        <td>
                          {allowReschedule ? (
                            <Input
                              id="datePicker"
                              type="text"
                              placeholder="Selecciona fecha"
                              defaultValue={service.scheduled_start.date}
                              className="form-control"
                            />
                          ) : (
                            <span>{service.scheduled_start.date}</span>
                          )}
                        </td>
                        <td>
                          {allowReschedule ? (
                            <Input
                              id="timePicker"
                              type="text"
                              placeholder="Selecciona hora"
                              defaultValue={service.scheduled_start.time}
                              className="form-control"
                            />
                          ) : (
                            <span>{service.scheduled_start.time}</span>
                          )}
                        </td>
                        <td>
                          {service.type === "OPEN"
                            ? i18n.t("OpenServices")
                            : service.type === "CLOSED"
                            ? i18n.t("ClosedServices")
                            : i18n.t(service.type)
                          }
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
              <p/>
              <div className="request-separator" />
            </Col>
          )}
          <Col xxl={12} md={12} xs={12} className="pt-2 d-flex">
            <button 
              onClick={handleReschedule} 
              className="btn btn-request"
              disabled={!allowReschedule}
            >
              {i18n.t("Reschedule")}
            </button>
            <button onClick={handleGoBack} className="btn btn-go-back mx-2">
              {i18n.t("GoBack")}
            </button>
          </Col>
        </Row>
      </CardBody>
    </Fragment>
  );
};

Step1.propTypes = {
  token: PropTypes.string.isRequired,
  infoUser: PropTypes.object.isRequired,
  setErrorAlert: PropTypes.func.isRequired,
  setErrorAlertMessage: PropTypes.func.isRequired,
};

export default Step1;
